import React, { useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import { fetchChecklistDetail } from "../../../../features/checks/checkActions";

const CheckProgressWidget = ({ slug, selectedDate }) => {
  const dispatch = useDispatch();

  // ============================================================
  // GET CHECKLIST DETAIL FROM REDUX
  // ============================================================

  const {
    data: checklistDetail,
    loading,
    error,
  } = useSelector((state) => state.checks.checklistDetail || {});

  // ============================================================
  // FETCH DETAIL WHEN SLUG / DATE CHANGES
  // ============================================================

  useEffect(() => {
    if (!slug || !selectedDate) return;

    dispatch(
      fetchChecklistDetail({
        slug,
        creationDate: selectedDate,
      }),
    );
  }, [dispatch, slug, selectedDate]);

  // ============================================================
  // VALUES FROM API
  // ============================================================

  const totalCount = checklistDetail?.totalChecklistItems ?? 0;

  const completedCount =
    checklistDetail?.completedChecklistItems ?? 0;

  const passedCount = checklistDetail?.passed ?? 0;

  const issuesCount = checklistDetail?.issued ?? 0;

  const pendingCount = checklistDetail?.pending ?? 0;

  const percentage =
    checklistDetail?.completionPercentage ?? 0;

  // SVG Radial Circle geometry
  const radius = 50;
  const strokeWidth = 10;
  const circumference = 2 * Math.PI * radius;

  const strokeDashoffset =
    circumference - (percentage / 100) * circumference;

  // ============================================================
  // LOADING
  // ============================================================

  // if (loading) {
  //   return (
  //     <div className="w-full">
  //       <h3 className="text-sm font-bold text-slate-900 w-full text-left mb-5">
  //         Check Progress
  //       </h3>

  //       <div className="flex items-center justify-center w-36 h-36">
  //         <div className="w-8 h-8 border-2 border-slate-200 border-t-emerald-600 rounded-full animate-spin" />
  //       </div>
  //     </div>
  //   );
  // }

  // ============================================================
  // ERROR
  // ============================================================

  if (error) {
    return (
      <div className="w-full">
        <h3 className="text-sm font-bold text-slate-900 w-full text-left mb-5">
          Check Progress
        </h3>

        <p className="text-xs text-red-500">
          {error}
        </p>
      </div>
    );
  }

  return (
    <div className="w-full">

      {/* Title Label */}
      <h3 className="text-sm font-bold text-slate-900 w-full text-left mb-5">
        Check Progress
      </h3>

      {/* Radial Donut Progress Ring Block */}
      <div className="relative w-36 h-36 flex items-center justify-center select-none m-auto">
        <svg
          className="w-full h-full transform -rotate-90"
          viewBox="0 0 120 120"
        >
          {/* Base Gray Background Circle */}
          <circle
            cx="60"
            cy="60"
            r={radius}
            className="stroke-slate-100"
            strokeWidth={strokeWidth}
            fill="transparent"
          />

          {/* Active Green Progress Circle */}
          <circle
            cx="60"
            cy="60"
            r={radius}
            className="stroke-emerald-600 transition-all duration-500 ease-out"
            strokeWidth={strokeWidth}
            strokeDasharray={circumference}
            strokeDashoffset={strokeDashoffset}
            strokeLinecap="round"
            fill="transparent"
          />
        </svg>

        {/* Center Counter */}
        <div className="absolute text-center flex flex-col items-center justify-center">
          <p className="text-xl font-bold text-slate-800 leading-none">
            {completedCount}{" "}
            <span className="text-xs text-slate-400 font-medium">
              / {totalCount}
            </span>
          </p>

          <p className="text-[10px] text-slate-400 font-medium mt-1">
            Completed
          </p>
        </div>
      </div>

      {/* Percentage */}
      <p className="text-xs font-bold text-slate-800 mt-4 mb-6 select-none">
        {percentage}% complete
      </p>

      {/* Status Metrics */}
      <div className="w-full flex flex-col gap-2.5 border-t border-slate-50 pt-4">

        {/* Passed */}
        <div className="flex items-center justify-between text-xs font-medium">
          <div className="flex items-center gap-2 text-slate-600">
            <span className="w-2 h-2 rounded-full bg-emerald-500 shrink-0" />
            <span>Passed</span>
          </div>

          <span className="font-bold text-slate-800">
            {passedCount}
          </span>
        </div>

        {/* Issues */}
        <div className="flex items-center justify-between text-xs font-medium">
          <div className="flex items-center gap-2 text-slate-600">
            <span className="w-2 h-2 rounded-full bg-red-500 shrink-0" />
            <span>Issues</span>
          </div>

          <span className="font-bold text-slate-800">
            {issuesCount}
          </span>
        </div>

        {/* Pending */}
        <div className="flex items-center justify-between text-xs font-medium">
          <div className="flex items-center gap-2 text-slate-600">
            <span className="w-2 h-2 rounded-full bg-slate-300 shrink-0" />
            <span>Pending</span>
          </div>

          <span className="font-bold text-slate-800">
            {pendingCount}
          </span>
        </div>

      </div>
    </div>
  );
};

export default CheckProgressWidget;